import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

export const THEME_STORAGE_KEY = "zunoTheme";

const ThemeContext = createContext(null);

export function getStoredTheme() {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

export function resolveInitialTheme(storedTheme) {
  if (storedTheme === "light" || storedTheme === "dark") return storedTheme;
  return "dark";
}

export function getInitialTheme() {
  return resolveInitialTheme(getStoredTheme());
}

export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "light" ? "#F5F7FB" : "#0a0e1a");
}

export function initializeTheme() {
  const theme = getInitialTheme();
  applyTheme(theme);
  return theme;
}

async function syncNativeChrome(theme) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setBackgroundColor({ color: theme === "light" ? "#F5F7FB" : "#0a0e1a" });
    await StatusBar.setStyle({ style: theme === "light" ? Style.Dark : Style.Light });
  } catch (error) {
    if (process.env.NODE_ENV === "development") console.warn("StatusBar theme sync failed", error);
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    syncNativeChrome(theme);
  }, [theme]);

  const setTheme = useCallback((nextTheme) => {
    const normalized = nextTheme === "light" ? "light" : "dark";
    try { window.localStorage.setItem(THEME_STORAGE_KEY, normalized); } catch {}
    setThemeState(normalized);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [setTheme, theme]);

  const value = useMemo(() => ({
    theme,
    isDark: theme === "dark",
    setTheme,
    toggleTheme,
  }), [setTheme, theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme deve ser usado dentro de ThemeProvider");
  return context;
}
